import React, { useState } from 'react';
import { IntakeStep, GoalBalanceCategory } from './types';
import { GoalType, GoalQuestionnaireAnswers, GoalCommitment, GoalMainCategory } from '../Milestone_organizer/types';
import GoalIntake from './GoalIntake';
import GoalTypeQuestionnaire from './GoalTypeQuestionnaire';
import GoalTypeConfirmation from './GoalTypeConfirmation';
import MissionVisionStep from './MissionVisionStep';
import CommitmentStep from './CommitmentStep';
import GoalCategorySelector from './GoalCategorySelector';

/**
 * IntakeFlow - Hoofdcomponent voor Fase 1: Goal Intake & Commitment
 * 
 * Flow:
 * 1. GoalIntake - Wat is je doel? + Levensbalans categorie
 * 2. GoalTypeQuestionnaire - 4 vragen voor type bepaling
 * 3. GoalTypeConfirmation - Bevestig het goal type
 * 4. MissionVisionStep - Formuleer Missie & Visie
 * 5. CommitmentStep - Onderteken en start
 * 
 * Na completion wordt onComplete aangeroepen met alle verzamelde data.
 */

interface IntakeFlowProps {
  userId: string;
  onComplete: (data: {
    title: string;
    description?: string;
    startDate: string;
    endDate: string;
    goalType: GoalType;
    questionnaireAnswers: GoalQuestionnaireAnswers;
    mission: string;
    vision: string;
    commitment: GoalCommitment;
    balanceCategory: GoalBalanceCategory;
    goalMainCategory?: GoalMainCategory;
  }) => void;
  onCancel: () => void;
}

interface IntakeData {
  title: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  goalType?: GoalType;
  suggestedType?: GoalType;
  questionnaireAnswers?: GoalQuestionnaireAnswers;
  mission?: string;
  vision?: string;
  balanceCategory?: GoalBalanceCategory;
  goalMainCategory?: GoalMainCategory;
}

const IntakeFlow: React.FC<IntakeFlowProps> = ({ userId, onComplete, onCancel }) => {
  const [step, setStep] = useState<IntakeStep>('category');
  const [data, setData] = useState<IntakeData>({ title: '', balanceCategory: 'work' });

  // Step 0: Category selected
  const handleCategorySelect = (category: GoalMainCategory) => {
    setData(prev => ({ ...prev, goalMainCategory: category }));
    setStep('intake');
  };

  // Step 1: Goal intake complete
  const handleIntakeNext = (title: string, description?: string, startDate?: string, endDate?: string, balanceCategory?: GoalBalanceCategory) => {
    setData(prev => ({ ...prev, title, description, startDate, endDate, balanceCategory: balanceCategory || 'work' }));
    setStep('questionnaire');
  };

  // Step 2: Questionnaire complete
  const handleQuestionnaireNext = (answers: GoalQuestionnaireAnswers, suggestedType: GoalType) => {
    setData(prev => ({ ...prev, questionnaireAnswers: answers, suggestedType }));
    setStep('confirmation');
  };

  // Step 3: Type confirmation complete
  const handleTypeConfirm = (confirmedType: GoalType) => {
    setData(prev => ({ ...prev, goalType: confirmedType }));
    setStep('missionvision');
  };

  // Step 4: Mission & Vision complete
  const handleMissionVisionNext = (mission: string, vision: string) => {
    setData(prev => ({ ...prev, mission, vision }));
    setStep('commitment');
  };

  // Step 5: Commitment complete
  const handleCommit = (commitment: GoalCommitment) => {
    if (data.title && data.goalType && data.questionnaireAnswers && data.mission && data.vision && data.startDate && data.endDate) {
      onComplete({
        title: data.title,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
        goalType: data.goalType,
        questionnaireAnswers: data.questionnaireAnswers,
        mission: data.mission,
        vision: data.vision,
        commitment,
        balanceCategory: data.balanceCategory || 'work',
        goalMainCategory: data.goalMainCategory,
      });
    }
  };

  // Navigation back handlers
  const handleBackToCategory = () => setStep('category');
  const handleBackToIntake = () => setStep('intake');
  const handleBackToQuestionnaire = () => setStep('questionnaire');
  const handleBackToConfirmation = () => setStep('confirmation');
  const handleBackToMissionVision = () => setStep('missionvision');

  const PROGRESS_STEPS = ['intake', 'questionnaire', 'confirmation', 'missionvision', 'commitment'];
  const progressIndex = PROGRESS_STEPS.indexOf(step);

  return (
    <div className="min-h-[400px] py-8 px-4">

      {/* Category selector — no step indicator, full screen */}
      {step === 'category' && (
        <GoalCategorySelector
          onSelect={handleCategorySelect}
          onCancel={onCancel}
        />
      )}

      {step !== 'category' && (
        <>
          {/* Step indicator */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="flex items-center justify-center gap-2">
              {PROGRESS_STEPS.map((s, i) => {
                const isActive = i === progressIndex;
                const isComplete = i < progressIndex;

                return (
                  <React.Fragment key={s}>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-orange-500 text-white'
                          : isComplete
                            ? 'bg-orange-200 text-orange-700'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isComplete ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    {i < PROGRESS_STEPS.length - 1 && (
                      <div className={`w-8 h-0.5 ${i < progressIndex ? 'bg-orange-300' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Cancel / back to category */}
          <div className="max-w-xl mx-auto mb-4 flex items-center gap-4">
            <button
              onClick={handleBackToCategory}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              ← Wijzig type
            </button>
            {data.goalMainCategory && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-gray-200 text-gray-500 capitalize">
                {data.goalMainCategory === 'personal' ? 'Persoonlijk'
                  : data.goalMainCategory === 'business' ? 'Zakelijk'
                  : 'Samen'}
              </span>
            )}
            <button
              onClick={onCancel}
              className="ml-auto text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Annuleren
            </button>
          </div>

          {/* Current step */}
          {step === 'intake' && (
            <GoalIntake
              onNext={handleIntakeNext}
              initialTitle={data.title}
              initialDescription={data.description}
              initialStartDate={data.startDate}
              initialEndDate={data.endDate}
              initialBalanceCategory={data.balanceCategory}
            />
          )}

          {step === 'questionnaire' && (
            <GoalTypeQuestionnaire
              onNext={handleQuestionnaireNext}
              onBack={handleBackToIntake}
            />
          )}

          {step === 'confirmation' && data.suggestedType && (
            <GoalTypeConfirmation
              suggestedType={data.suggestedType}
              onConfirm={handleTypeConfirm}
              onBack={handleBackToQuestionnaire}
            />
          )}

          {step === 'missionvision' && (
            <MissionVisionStep
              initialMission={data.mission}
              initialVision={data.vision}
              onNext={handleMissionVisionNext}
              onBack={handleBackToConfirmation}
            />
          )}

          {step === 'commitment' && (
            <CommitmentStep
              goalTitle={data.title}
              userId={userId}
              onCommit={handleCommit}
              onBack={handleBackToMissionVision}
            />
          )}
        </>
      )}
    </div>
  );
};

export default IntakeFlow;
